import { getColumn, getTable } from './schema.js';
import { invalidInput, unsupported } from './errors.js';
const RESERVED_QUERY_KEYS = new Set([
    'select',
    'order',
    'limit',
    'offset',
    'columns',
    'on_conflict',
    'and',
    'or',
    'not',
]);
const SUPPORTED_FILTER_OPS = new Set([
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'in',
    'is',
    'like',
    'ilike',
]);
const SUPPORTED_METHODS = new Set(['GET', 'HEAD', 'POST', 'PATCH', 'DELETE']);
export function quoteIdentifier(name) {
    return `"${name.replace(/"/g, '""')}"`;
}
export function parseRequest(request, tables) {
    if (request.url.length > 32768)
        throw invalidInput('Request URL exceeds the 32768-character limit.');
    const url = new URL(request.url);
    const method = (request.method || 'GET').toUpperCase();
    if (!SUPPORTED_METHODS.has(method)) {
        throw unsupported(`DemoPack does not handle unsupported requests: ${method} ${url.pathname}`);
    }
    if (url.pathname.includes('/rpc/') || url.pathname.endsWith('/rpc')) {
        throw unsupported(`Unsupported DemoPack RPC call: ${url.pathname}`);
    }
    if (!url.pathname.startsWith('/rest/v1/')) {
        throw unsupported(`Unsupported DemoPack request target: ${url.pathname}`);
    }
    const tableName = url.pathname.slice('/rest/v1/'.length);
    if (!tableName || tableName.includes('/')) {
        throw unsupported(`Unsupported DemoPack request target: ${url.pathname}`);
    }
    const table = getTable(tableName, tables);
    if (!table) {
        throw unsupported(`DemoPack only supports configured tables. Received: ${tableName}`);
    }
    const prefer = parsePrefer(request.headers.get('prefer'));
    assertSupportedHeaders(request, prefer);
    if (url.searchParams.has('on_conflict') || url.searchParams.has('upsert')) {
        throw unsupported('Unsupported DemoPack upsert request.');
    }
    const params = [];
    url.searchParams.forEach((value, key) => {
        params.push([key, value]);
    });
    for (const key of RESERVED_QUERY_KEYS) {
        if (url.searchParams.getAll(key).length > 1)
            throw unsupported(`Duplicate query parameter: ${key}`);
    }
    if (params.length > 128)
        throw invalidInput('Request exceeds the 128-parameter limit.');
    const filters = [];
    let insertColumns = null;
    for (const [key, value] of params) {
        if (key.includes('.')) {
            throw unsupported(`Unsupported DemoPack referenced-table parameter: ${key}`);
        }
        if (key === 'and' || key === 'or' || key === 'not') {
            throw unsupported(`Unsupported DemoPack query parameters for ${method}: ${key}`);
        }
        if (key === 'columns') {
            if (method !== 'POST')
                throw unsupported('columns is only supported for inserts.');
            insertColumns = parseColumnList(table, value);
            continue;
        }
        if (RESERVED_QUERY_KEYS.has(key)) {
            continue;
        }
        filters.push(parseFilter(table, key, value));
    }
    const selectParam = url.searchParams.get('select');
    const selectColumns = parseSelect(table, selectParam);
    const order = parseOrder(table, url.searchParams.get('order'));
    const limit = parseIntegerParam('limit', url.searchParams.get('limit'));
    const offset = parseIntegerParam('offset', url.searchParams.get('offset'));
    if (method === 'POST' && filters.length > 0)
        throw unsupported('Filters on inserts are unsupported.');
    if (!['GET', 'HEAD'].includes(method) && (order.length || limit !== null || offset !== null)) {
        throw unsupported('Ordering and pagination of mutations are unsupported.');
    }
    return {
        method,
        table,
        selectColumns,
        filters,
        order,
        limit,
        offset,
        wantsCount: prefer.count === 'exact',
        returnRepresentation: prefer.returnRepresentation || Boolean(selectParam),
        missingDefault: prefer.missingDefault,
        singleObject: prefer.singleObject || isObjectAccept(request.headers.get('accept')),
        insertColumns,
    };
}
function parsePrefer(header) {
    const state = {
        count: null,
        returnRepresentation: false,
        missingDefault: false,
        singleObject: false,
    };
    if (!header) {
        return state;
    }
    for (const part of header.split(',').map((item) => item.trim()).filter(Boolean)) {
        const normalized = part.toLowerCase();
        if (normalized === 'count=exact') {
            state.count = 'exact';
            continue;
        }
        if (normalized === 'count=planned' || normalized === 'count=estimated') {
            throw unsupported(`Unsupported DemoPack count mode: ${part}`);
        }
        if (normalized === 'return=representation') {
            state.returnRepresentation = true;
            continue;
        }
        if (normalized === 'return=minimal') {
            state.returnRepresentation = false;
            continue;
        }
        if (normalized === 'missing=default') {
            state.missingDefault = true;
            continue;
        }
        if (normalized.includes('resolution=merge-duplicates') || normalized.includes('resolution=ignore-duplicates')) {
            throw unsupported('Unsupported DemoPack upsert request. Prefer header includes duplicate-resolution behavior.');
        }
        throw unsupported(`Unsupported DemoPack Prefer option: ${part}`);
    }
    return state;
}
function assertSupportedHeaders(request, prefer) {
    const acceptProfile = request.headers.get('accept-profile');
    const contentProfile = request.headers.get('content-profile');
    if (acceptProfile && acceptProfile !== 'public') {
        throw unsupported(`Unsupported DemoPack schema profile: ${acceptProfile}`);
    }
    if (contentProfile && contentProfile !== 'public') {
        throw unsupported(`Unsupported DemoPack content profile: ${contentProfile}`);
    }
    const accept = request.headers.get('accept');
    if (accept && !['*/*', 'application/json', 'application/vnd.pgrst.object+json'].includes(accept.toLowerCase())) {
        throw unsupported(`Unsupported response format: ${accept}`);
    }
    if (request.headers.has('range') || request.headers.has('range-unit')) {
        throw unsupported('Range headers are unsupported; use the SDK range() method.');
    }
    void prefer;
}
function isObjectAccept(accept) {
    if (!accept) {
        return false;
    }
    return accept.split(',').some((part) => part.trim().toLowerCase().startsWith('application/vnd.pgrst.object+json'));
}
function parseSelect(table, selectParam) {
    if (!selectParam || selectParam === '*') {
        return '*';
    }
    if (/[():.]/.test(selectParam)) {
        throw unsupported(`Unsupported select expression: ${selectParam}`);
    }
    return parseColumnList(table, selectParam).map((name) => {
        const column = getColumn(table, name);
        if (!column) {
            throw unsupported(`Unsupported select columns: ${name}`);
        }
        return column;
    });
}
function parseColumnList(table, raw) {
    const items = raw
        .split(',')
        .map((value) => value.trim());
    if (items.some((value) => !/^(?:[A-Za-z_][A-Za-z0-9_]*|"[A-Za-z_][A-Za-z0-9_]*")$/.test(value))) {
        throw unsupported(`Unsupported column list: ${raw}`);
    }
    for (let index = 0; index < items.length; index += 1)
        items[index] = items[index].replace(/^"|"$/g, '');
    if (items.length === 0) {
        throw unsupported(`Unsupported select columns: ${raw}`);
    }
    const unknown = items.filter((item) => !getColumn(table, item));
    if (unknown.length > 0) {
        throw unsupported(`Unsupported select columns: ${unknown.join(', ')}`);
    }
    return items;
}
function parseOrder(table, raw) {
    if (!raw) {
        return [];
    }
    return raw.split(',').map((part) => {
        const tokens = part.trim().split('.');
        const columnName = tokens[0];
        if (!columnName) {
            throw unsupported(`Unsupported order clause: ${raw}`);
        }
        const column = getColumn(table, columnName);
        if (!column) {
            throw unsupported(`Unsupported order column: ${columnName}`);
        }
        if (tokens.filter((part) => part === 'asc' || part === 'desc').length > 1 ||
            tokens.filter((part) => part === 'nullsfirst' || part === 'nullslast').length > 1) {
            throw unsupported(`Conflicting order modifiers: ${part}`);
        }
        let ascending = true;
        let nulls = null;
        for (const token of tokens.slice(1)) {
            if (token === 'asc') {
                ascending = true;
            }
            else if (token === 'desc') {
                ascending = false;
            }
            else if (token === 'nullsfirst') {
                nulls = 'first';
            }
            else if (token === 'nullslast') {
                nulls = 'last';
            }
            else {
                throw unsupported(`Unsupported order modifier: ${token}`);
            }
        }
        return { column, ascending, nulls };
    });
}
function parseIntegerParam(name, raw) {
    if (raw === null) {
        return null;
    }
    if (!/^\d+$/.test(raw)) {
        throw invalidInput(`Unsupported DemoPack query value for ${name}: ${raw}`);
    }
    const value = Number(raw);
    if (!Number.isSafeInteger(value))
        throw invalidInput(`Integer is outside the supported safe range: ${raw}`);
    return value;
}
function parseFilter(table, columnName, raw) {
    const column = getColumn(table, columnName);
    if (!column) {
        throw unsupported(`Unsupported filter column: ${columnName}`);
    }
    const dot = raw.indexOf('.');
    if (dot <= 0) {
        throw unsupported(`Unsupported filter expression for ${columnName}: ${raw}`);
    }
    const operator = raw.slice(0, dot);
    const valueText = raw.slice(dot + 1);
    if (operator === 'not' || operator.startsWith('not.')) {
        throw unsupported(`Unsupported DemoPack filter operator: ${operator}`);
    }
    if (!SUPPORTED_FILTER_OPS.has(operator)) {
        throw unsupported(`Unsupported DemoPack filter operator: ${operator}`);
    }
    if (operator === 'is') {
        if (valueText.toLowerCase() !== 'null') {
            throw unsupported(`Unsupported DemoPack is() value: ${valueText}`);
        }
        return { column, operator: 'is', value: null };
    }
    if (operator === 'in') {
        return { column, operator: 'in', value: parseInList(column, valueText) };
    }
    if (operator === 'like' || operator === 'ilike') {
        return { column, operator, value: valueText };
    }
    return {
        column,
        operator: operator,
        value: coerceValue(column, valueText),
    };
}
export function parseInList(column, raw) {
    if (!raw.startsWith('(') || !raw.endsWith(')'))
        throw invalidInput(`Unsupported in() list: ${raw}`);
    const inner = raw.slice(1, -1);
    if (!inner)
        return [];
    const values = [];
    let index = 0;
    while (index < inner.length) {
        let current = '';
        if (inner[index] === '"') {
            index += 1;
            let closed = false;
            while (index < inner.length) {
                const char = inner[index++];
                if (char === '\\') {
                    if (index === inner.length)
                        throw invalidInput('Unterminated in() escape.');
                    current += inner[index++];
                }
                else if (char === '"') {
                    closed = true;
                    break;
                }
                else
                    current += char;
            }
            if (!closed)
                throw invalidInput('Unterminated quoted in() value.');
        }
        else {
            while (index < inner.length && inner[index] !== ',') {
                const char = inner[index++];
                if (char === '"' || char === '(' || char === ')')
                    throw invalidInput('Malformed in() value.');
                current += char;
            }
            if (!current)
                throw invalidInput('Empty unquoted in() value.');
        }
        if (values.length >= 1000)
            throw invalidInput('in() exceeds the 1000-value limit.');
        values.push(coerceValue(column, current));
        if (index === inner.length)
            break;
        if (inner[index++] !== ',' || index === inner.length)
            throw invalidInput('Malformed in() separator.');
    }
    return values;
}
export function coerceValue(column, raw) {
    if (column.type === 'integer') {
        if (!/^-?\d+$/.test(raw)) {
            throw invalidInput(`Expected integer value for ${column.name}, received: ${raw}`);
        }
        const value = Number(raw);
        if (!Number.isSafeInteger(value))
            throw invalidInput(`Unsafe integer for ${column.name}: ${raw}`);
        return value;
    }
    return raw;
}
export function likePattern(raw) {
    // PostgREST replaces the * alias and leaves PostgreSQL backslash escapes intact.
    return raw.replace(/\*/g, '%');
}
